/**
 * AssignmentTemplate Service — reusable «who» catalog for workflow nodes.
 * Rule: NO UI IN SDK
 */
import {
    CreateAssignmentTemplateApiSchema,
    UpdateAssignmentTemplateApiSchema,
} from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class AssignmentTemplateService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(() => this.api.getAll(params), { maxRetries: 2 });
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'Assignment templates fetched',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch assignment templates');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(() => this.api.getById(id), { maxRetries: 2 });
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch assignment template');
        }
    }

    async getUnitHeadOptions() {
        try {
            const response = await withRetry(() => this.api.getUnitHeadOptions(), { maxRetries: 2 });
            return {
                success: true,
                data: response.data || [],
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch unit-head options');
        }
    }

    async create(data) {
        const validation = CreateAssignmentTemplateApiSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid assignment template data', validation.error.format());
        }
        try {
            const response = await this.api.create(validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Assignment template created',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create assignment template');
        }
    }

    async update(id, data) {
        const validation = UpdateAssignmentTemplateApiSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid assignment template data', validation.error.format());
        }
        try {
            const response = await this.api.update(id, validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Assignment template updated',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update assignment template');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Assignment template deleted',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete assignment template');
        }
    }

    async preview(payload) {
        try {
            const response = await this.api.preview(payload);
            return {
                success: true,
                data: response.data,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to preview assignment template');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
